<template>
  <div>
    <base-header
      type="gradient-danger"
      class="pb-6 pb-8 pt-5 pt-md-8"
    ></base-header>
    <div class="container-fluid mt--9">
      <div class="col-12 mb-3 mb-xl-0">
        <div class="card shadow legacy-card">
          <div class="legacy-body">
            <div class="legacy-icon">
              <i class="fas fa-rocket"></i>
            </div>
            <h2 class="legacy-title">
              {{ title }}
            </h2>
            <p v-if="legacy && legacy.text" class="legacy-text">
              {{ legacy.text }}
            </p>
            <p v-else class="legacy-text">
              Ahora solo está disponible en la <strong>nueva consola</strong> de
              Canplast.
            </p>
            <a
              v-if="newConsoleUrl"
              :href="newConsoleUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-legacy"
            >
              <i class="fas fa-external-link-alt mr-2"></i>
              Ir a la nueva consola
            </a>

            <button
              v-if="help"
              type="button"
              class="legacy-help-toggle"
              @click="showHelp = !showHelp"
            >
              <i class="fas fa-question-circle mr-1"></i>
              ¿Dónde lo encuentro?
            </button>

            <div v-if="help && showHelp" class="legacy-help">
              <button
                type="button"
                class="legacy-help-close"
                aria-label="Cerrar"
                @click="showHelp = false"
              >
                <i class="fas fa-times"></i>
              </button>
              <p class="legacy-help-text">
                En la nueva consola abre el menú de herramientas con el ícono
                <i class="fas fa-th"></i> de arriba a la derecha y elige
                <strong>{{ help.option }}</strong>. También puedes escribir
                «{{ help.search }}» en el buscador.
              </p>
              <img :src="help.image" :alt="help.alt" class="legacy-help-img" />
            </div>
          </div>

          <div v-if="legacy && !loading" class="legacy-request">
            <div class="legacy-request-icon">
              <i class="fas fa-unlock-alt"></i>
            </div>
            <h3 class="legacy-request-title">
              ¿Necesitas acceso temporal a la versión anterior?
            </h3>

            <div v-if="pending" class="legacy-request-status">
              <i class="fas fa-clock mr-2"></i>
              Ya enviaste una solicitud el {{ requestDate }}. Está en revisión.
            </div>

            <div
              v-else-if="sent"
              class="legacy-request-status legacy-request-status--sent"
            >
              <i class="fas fa-check mr-2"></i>
              Solicitud enviada. Te avisaremos cuando esté lista.
            </div>

            <div
              v-if="rejected && !sent"
              class="legacy-request-status legacy-request-status--rejected"
            >
              <i class="fas fa-times-circle mr-2"></i>
              <span>
                Tu solicitud del {{ requestDate }} fue rechazada.
                <span v-if="latestRequest.note">{{ latestRequest.note }}</span>
              </span>
            </div>

            <div v-if="!pending && !sent" class="legacy-request-form">
              <textarea
                v-model.trim="reason"
                class="form-control legacy-request-reason"
                rows="3"
                maxlength="300"
                placeholder="Explicanos por qué necesitas acceso en esta consola y qué no puedes hacer en la nueva. Tu solicitud será revisada por el equipo de TI y Desarrollo."
              ></textarea>
              <button
                type="button"
                class="btn legacy-request-button"
                :disabled="!reason || sending"
                @click="sendRequest"
              >
                <i class="fas fa-paper-plane mr-2"></i>
                {{ sending ? "Enviando…" : "Solicitar acceso temporal" }}
              </button>
              <p v-if="error" class="legacy-request-error">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth } from "firebase/auth";
import {
  notifyAdmins,
  hasLegacyAccess,
  legacyRouteKey,
  findLatestRequest,
  buildNewConsoleUrl,
  requestLegacyAccess,
} from "@/legacyAccess";

export default {
  name: "NewConsole",
  data() {
    return {
      sent: false,
      error: "",
      reason: "",
      sending: false,
      loading: true,
      showHelp: false,
      latestRequest: null,
    };
  },
  mounted() {
    this.load();
  },
  watch: {
    "$route.query.from": "load",
  },
  methods: {
    async load() {
      this.sent = false;
      this.error = "";
      this.reason = "";
      this.showHelp = false;
      this.loading = true;
      this.latestRequest = null;
      const user = getAuth().currentUser;
      if (!user || !this.legacy) return;
      try {
        if (await hasLegacyAccess(user.email, this.accessKey)) {
          this.$router.replace(this.$route.query.from);
          return;
        }
        this.latestRequest = await findLatestRequest(user.email, this.accessKey);
        this.loading = false;
      } catch (error) {
        console.error("Error al buscar solicitudes anteriores: ", error);
        this.loading = false;
      }
    },
    async sendRequest() {
      const user = getAuth().currentUser;
      if (!user) return;
      this.error = "";
      this.sending = true;
      try {
        const request = {
          name: user.displayName || "",
          email: user.email,
          path: this.accessKey,
          module: this.moduleName,
          reason: this.reason,
        };
        await requestLegacyAccess(request);
        this.sent = true;
        notifyAdmins(request).catch((error) => {
          console.error("Error al notificar la solicitud: ", error);
        });
      } catch (error) {
        console.error("Error al enviar la solicitud: ", error);
        this.error =
          "No se pudo enviar la solicitud. Intenta de nuevo o avisa a Sistemas.";
      } finally {
        this.sending = false;
      }
    },
  },
  computed: {
    pending() {
      return !!this.latestRequest && this.latestRequest.status === "pending";
    },
    rejected() {
      return !!this.latestRequest && this.latestRequest.status === "rejected";
    },
    requestDate() {
      if (!this.latestRequest || !this.latestRequest.requestedAt) return "";
      return this.latestRequest.requestedAt.toLocaleDateString("es-MX", {
        day: "numeric",
        month: "long",
      });
    },
    originRoute() {
      const from = this.$route.query.from;
      if (!from) return null;
      return this.$router.resolve(from).route;
    },
    legacy() {
      return this.originRoute ? this.originRoute.meta.legacy || null : null;
    },
    accessKey() {
      return this.originRoute ? legacyRouteKey(this.originRoute) : null;
    },
    moduleName() {
      return this.legacy ? this.legacy.name : "Esta sección";
    },
    help() {
      return this.legacy ? this.legacy.help || null : null;
    },
    title() {
      return this.legacy && this.legacy.title
        ? this.legacy.title
        : `${this.moduleName} ahora vive en la nueva consola`;
    },
    newConsoleUrl() {
      if (!this.legacy) return "https://consola-v2.canplast.net/inicio";
      if (!this.legacy.newConsoleUrl) return null;
      return buildNewConsoleUrl(this.legacy.newConsoleUrl, this.originRoute.params);
    },
  },
};
</script>

<style scoped>
.legacy-card {
  border: none;
  overflow: hidden;
  background: linear-gradient(160deg, #fff5f5 0%, #fff 55%, #fff8f8 100%);
}
.legacy-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 40px;
}
.legacy-icon {
  font-size: 3.5rem;
  color: #f5365c;
  margin-bottom: 20px;
  animation: legacy-bounce 1.4s infinite;
}
@keyframes legacy-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}
.legacy-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 14px;
}
.legacy-text {
  font-size: 1.05rem;
  color: #525f7f;
  max-width: 520px;
  line-height: 1.7;
  margin-bottom: 32px;
}
.btn-legacy {
  display: inline-flex;
  align-items: center;
  padding: 14px 36px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 50px;
  color: #fff !important;
  background: linear-gradient(87deg, #f5365c 0, #f55f36 200%);
  border: none;
  box-shadow: 0 6px 20px rgba(245, 54, 92, 0.35);
  text-decoration: none;
  transition: transform 0.15s, box-shadow 0.15s;
}
.btn-legacy:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(245, 54, 92, 0.45);
}
.legacy-help-toggle {
  display: inline-flex;
  align-items: center;
  margin-top: 18px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.9rem;
  font-weight: 600;
  color: #f5365c;
  cursor: pointer;
}
.legacy-help-toggle:hover {
  text-decoration: underline;
}
.legacy-help {
  position: relative;
  width: 100%;
  max-width: 560px;
  margin-top: 16px;
  padding: 16px 18px 14px;
  border-radius: 12px;
  border: 1px solid rgba(245, 54, 92, 0.2);
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  color: #525f7f;
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: left;
}
.legacy-help-close {
  position: absolute;
  top: 8px;
  right: 10px;
  border: 0;
  background: none;
  color: #8898aa;
  font-size: 14px;
  cursor: pointer;
}
.legacy-help-close:hover {
  color: #32325d;
}
.legacy-help-text {
  margin: 0 22px 12px 0;
}
.legacy-help-text strong {
  color: #32325d;
}
.legacy-help-img {
  display: block;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.legacy-request {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 40px 48px;
  border-top: 1px solid rgba(245, 54, 92, 0.08);
}
.legacy-request-icon {
  font-size: 2rem;
  color: #f5365c;
  margin-bottom: 14px;
}
.legacy-request-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 24px;
}
.legacy-request-form {
  width: 100%;
  max-width: 560px;
}
.legacy-request-reason {
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #525f7f;
  resize: vertical;
  box-shadow: none;
}
.legacy-request-reason:focus {
  border-color: #f5365c;
  box-shadow: 0 0 0 3px rgba(245, 54, 92, 0.12);
}
.legacy-request-button {
  display: inline-flex;
  align-items: center;
  padding: 12px 32px;
  font-size: 0.95rem;
  font-weight: 700;
  border-radius: 50px;
  color: #fff;
  background: #32325d;
  border: none;
  box-shadow: 0 4px 14px rgba(50, 50, 93, 0.25);
  transition: transform 0.15s, box-shadow 0.15s;
}
.legacy-request-button:hover {
  transform: translateY(-2px);
  color: #fff;
  box-shadow: 0 8px 22px rgba(50, 50, 93, 0.3);
}
.legacy-request-status {
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border-radius: 50px;
  background: #fff;
  color: #525f7f;
  font-size: 0.95rem;
  box-shadow: 0 4px 14px rgba(50, 50, 93, 0.08);
}
.legacy-request-status--sent {
  color: #2dce89;
}
.legacy-request-status--rejected {
  margin-bottom: 20px;
  color: #f5365c;
  text-align: left;
}
.legacy-request-error {
  margin-top: 14px;
  margin-bottom: 0;
  color: #f5365c;
  font-size: 0.9rem;
}
</style>
